<template>
  <div class="ai-check-container">
    <!-- Header Banner -->
    <div class="ai-header-banner">
      <div class="banner-overlay"></div>
      <div class="banner-content">
        <div class="banner-left">
          <div class="ai-icon-pulse">
            <i class="el-icon-connection"></i>
          </div>
          <div>
            <h1 class="banner-title">QUẢN LÝ CẤU HÌNH WEBHOOK</h1>
            <p class="banner-subtitle">Thiết lập kết nối nhận phản hồi sự kiện real-time, khóa xác thực bảo mật và đăng ký lắng nghe sự kiện tài khoản.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="isInitialLoading" class="glass-card loading-container flex flex-column justify-center align-center">
      <i class="el-icon-loading" style="font-size: 40px; color: #3b82f6; margin-bottom: 12px;"></i>
      <p style="color: #64748b; font-weight: 600;">Đang tải cấu hình Webhook...</p>
    </div>

    <!-- Main Content -->
    <div v-else class="glass-card panel-container">
      <el-tabs v-model="activeTab" type="card" class="modern-tabs">
        
        <!-- Tab 1: Webhook configuration -->
        <el-tab-pane label="Cấu hình Webhook" name="config">
          <div class="tab-inner-content">
            <!-- Hints -->
            <div class="hint-panel blue-hint mb-4">
              <div class="hint-title">
                <i class="el-icon-info"></i> Cơ chế hoạt động của Webhook
              </div>
              <ul class="hint-list">
                <li>Webhook gửi dữ liệu sự kiện phát sinh từ cổng Bank Hub về hệ thống máy chủ của bạn một cách chủ động.</li>
                <li><strong>Webhook URL:</strong> Đường dẫn đích nhận thông tin HTTP POST request (yêu cầu chuẩn HTTPS).</li>
                <li><strong>Secret Key:</strong> Khóa bí mật dùng để tạo chữ ký mã hóa (gửi qua Header <code>X-Secret-Key</code>).</li>
                <li><strong>Active:</strong> Bật/Tắt hoạt động của Webhook. Khi tắt, hệ thống sẽ tạm ngừng gửi các sự kiện.</li>
              </ul>
            </div>

            <!-- Current status panels -->
            <div v-if="webhook" class="webhook-status-card mb-4">
              <div class="status-header">
                <h3 class="section-title mb-0"><i class="el-icon-odometer"></i> Thông số kết nối hiện tại</h3>
                <el-tag :type="webhook.active === 1 ? 'success' : 'danger'" effect="dark" class="status-tag">
                  {{ webhook.active === 1 ? 'Đang hoạt động' : 'Tạm dừng' }}
                </el-tag>
              </div>

              <div class="status-grid-info mt-3">
                <div class="info-item">
                  <span class="info-label">Webhook URL</span>
                  <span class="info-val code-font">{{ webhook.webhook_url }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Phương thức bảo mật (Auth Type)</span>
                  <span class="info-val"><el-tag type="info" size="mini">{{ webhook.auth_type }}</el-tag></span>
                </div>
                <div class="info-item">
                  <span class="info-label">Khóa bí mật (Secret Key)</span>
                  <span class="info-val code-font text-password">••••••••••••••••••••••••</span>
                </div>
              </div>

              <!-- Events Registered list -->
              <div class="registered-events-section mt-4">
                <span class="info-label mb-2 block">Các sự kiện đã đăng ký lắng nghe ({{ webhook.allow_events ? webhook.allow_events.length : 0 }})</span>
                <div class="events-tags-container">
                  <el-tag
                    v-if="webhook.allow_events && webhook.allow_events.length === 0"
                    type="info"
                    size="medium"
                  >
                    Chưa đăng ký sự kiện nào
                  </el-tag>
                  <el-tag
                    v-for="(event, index) in webhook.allow_events"
                    :key="index"
                    type="success"
                    size="medium"
                    class="event-tag-chip"
                  >
                    {{ event }}
                  </el-tag>
                </div>
              </div>
            </div>

            <!-- Empty webhook state -->
            <div v-else class="empty-state-card mb-4">
              <i class="el-icon-circle-close"></i>
              <p class="empty-text">Chưa có đường dẫn Webhook nào được cấu hình cho Merchant này.</p>
            </div>

            <!-- Action buttons -->
            <div class="action-row pt-2">
              <el-button type="primary" class="gradient-btn-small blue" icon="el-icon-edit" @click="showEditDialog">
                {{ webhook ? 'Chỉnh sửa' : 'Cấu hình mới' }} Webhook
              </el-button>
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 2: Events Reference -->
        <el-tab-pane label="Danh mục & Tham chiếu Sự kiện" name="events">
          <div class="tab-inner-content">
            <div class="hint-panel orange-hint mb-4">
              <div class="hint-title">
                <i class="el-icon-collection"></i> 13 Loại Sự kiện trên hệ thống
              </div>
              <p class="hint-text mb-0">Dưới đây là các sự kiện chi tiết mà hệ thống hỗ trợ gửi thông báo Webhook về máy chủ đối soát.</p>
            </div>

            <!-- Category 1: Link Token -->
            <div class="event-category-block mb-4">
              <h3 class="category-title"><i class="el-icon-key"></i> Link Token Events (3 sự kiện)</h3>
              <div class="events-grid">
                <div class="event-card-item">
                  <code class="ev-code">LINK_TOKEN_CREATED</code>
                  <p class="ev-desc">Phát sinh khi có yêu cầu khởi tạo Link Token mới thành công.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_TOKEN_REVOKED</code>
                  <p class="ev-desc">Phát sinh khi một Link Token bị hủy bỏ quyền liên kết.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_TOKEN_EXPIRED</code>
                  <p class="ev-desc">Phát sinh khi Link Token hết thời gian hiệu lực cho phép.</p>
                </div>
              </div>
            </div>

            <!-- Category 2: Link Session -->
            <div class="event-category-block mb-4">
              <h3 class="category-title"><i class="el-icon-monitor"></i> Link Session Events (7 sự kiện)</h3>
              <div class="events-grid">
                <div class="event-card-item">
                  <code class="ev-code">LINK_SESSION_INITIALIZED</code>
                  <p class="ev-desc">Bắt đầu khởi dựng phiên liên kết tài khoản ngân hàng.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_SESSION_REINITIALIZED</code>
                  <p class="ev-desc">Khởi dựng lại phiên liên kết ngân hàng từ trạng thái cũ.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_SESSION_STATE_CHANGED</code>
                  <p class="ev-desc">Trạng thái xử lý phiên liên kết thay đổi (đang xử lý, chờ OTP...).</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_SESSION_COMPLETED</code>
                  <p class="ev-desc">Phiên liên kết hoàn thành và kết nối tài khoản thành công.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_SESSION_FAILED</code>
                  <p class="ev-desc">Phiên liên kết tài khoản bị lỗi hoặc thất bại giữa chừng.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_SESSION_ABANDONED</code>
                  <p class="ev-desc">Người dùng chủ động từ bỏ/hủy ngang phiên liên kết.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">LINK_SESSION_EXPIRED</code>
                  <p class="ev-desc">Phiên liên kết quá thời gian phản hồi và tự động đóng.</p>
                </div>
              </div>
            </div>

            <!-- Category 3: Bank Account -->
            <div class="event-category-block mb-4">
              <h3 class="category-title"><i class="el-icon-bank"></i> Bank Account Events (3 sự kiện)</h3>
              <div class="events-grid">
                <div class="event-card-item">
                  <code class="ev-code">BANK_ACCOUNT_LINKED</code>
                  <p class="ev-desc">Tài khoản ngân hàng đã được liên kết chính thức vào Brand.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">BANK_ACCOUNT_UNLINKED</code>
                  <p class="ev-desc">Hủy bỏ liên kết tài khoản ngân hàng ra khỏi Brand quản lý.</p>
                </div>
                <div class="event-card-item">
                  <code class="ev-code">BANK_ACCOUNT_INACTIVATED</code>
                  <p class="ev-desc">Tài khoản ngân hàng bị hệ thống vô hiệu hóa ngắt kết nối.</p>
                </div>
              </div>
            </div>

            <!-- Quick update footer actions -->
            <div class="quick-actions-bar mt-4 pt-3">
              <el-button type="success" class="gradient-btn-small green" icon="el-icon-success" @click="quickUpdateAll">
                Đăng ký nhận tất cả sự kiện (["*"])
              </el-button>
              <el-button class="cancel-upload-btn" icon="el-icon-circle-close" @click="quickUpdateNone">
                Hủy đăng ký tất cả sự kiện
              </el-button>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- Dialog: Edit/Create Webhook -->
    <el-dialog
      :title="webhook ? 'CẬP NHẬT CẤU HÌNH WEBHOOK' : 'THIẾT LẬP WEBHOOK MỚI'"
      :visible.sync="dialogEditWebhook"
      width="780px"
      custom-class="modern-dialog"
      @close="resetWebhookForm"
    >
      <el-form ref="formWebhook" :model="formWebhook" label-position="top">
        <el-row :gutter="20">
          <el-col :span="16">
            <el-form-item label="Webhook URL nhận thông báo (Bắt buộc HTTPS):" prop="webhook_url">
              <el-input
                v-model="formWebhook.webhook_url"
                placeholder="https://yourdomain.com/sepay-callback"
                prefix-icon="el-icon-link"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Xác thực (Auth Type):" prop="auth_type">
              <el-select v-model="formWebhook.auth_type" style="width: 100%;">
                <el-option label="SECRET_KEY" value="SECRET_KEY" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="16">
            <el-form-item label="Khóa bí mật (Secret Key):" prop="secret_key">
              <el-input
                v-model="formWebhook.secret_key"
                placeholder="Nhập khóa bí mật bảo mật header..."
                prefix-icon="el-icon-key"
                type="password"
                show-password
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Trạng thái kích hoạt:" prop="active">
              <div class="switch-status-wrapper">
                <el-switch
                  v-model="formWebhook.active"
                  :active-value="1"
                  :inactive-value="0"
                  active-color="#10b981"
                  inactive-color="#cbd5e1"
                />
                <span class="status-label-switch font-semibold ml-2">
                  {{ formWebhook.active === 1 ? 'Bật hoạt động' : 'Tạm ngắt' }}
                </span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Lắng nghe các sự kiện cụ thể:">
          <div class="events-picker-box">
            <el-checkbox
              v-model="selectAllEvents"
              class="mb-3 block font-bold"
              @change="toggleAllEvents"
            >
              Chọn nhận tất cả các sự kiện (Ký hiệu "*")
            </el-checkbox>

            <div class="checkbox-grid-events" :class="{ 'opacity-50': selectAllEvents }">
              <el-checkbox
                v-for="event in availableEvents"
                :key="event"
                v-model="formWebhook.allow_events"
                :label="event"
                :disabled="selectAllEvents"
                class="checkbox-item"
              >
                {{ event }}
              </el-checkbox>
            </div>
          </div>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogEditWebhook = false">Hủy bỏ</el-button>
        <el-button type="primary" class="gradient-btn-small blue" :loading="isSavingWebhook" @click="saveWebhook">
          Xác nhận lưu cấu hình
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MerchantConfigService from "@/services/MerchantConfigService";

export default {
  name: "WebhookManagement",
  data() {
    return {
      webhook: null,
      formWebhook: {
        webhook_url: "",
        auth_type: "SECRET_KEY",
        secret_key: "",
        active: 1,
        allow_events: []
      },
      isInitialLoading: false,
      isSavingWebhook: false,
      dialogEditWebhook: false,
      activeTab: "config",
      selectAllEvents: false,
      availableEvents: [
        "LINK_TOKEN_CREATED",
        "LINK_TOKEN_REVOKED",
        "LINK_TOKEN_EXPIRED",
        "LINK_SESSION_INITIALIZED",
        "LINK_SESSION_REINITIALIZED",
        "LINK_SESSION_STATE_CHANGED",
        "LINK_SESSION_COMPLETED",
        "LINK_SESSION_FAILED",
        "LINK_SESSION_ABANDONED",
        "LINK_SESSION_EXPIRED",
        "BANK_ACCOUNT_LINKED",
        "BANK_ACCOUNT_UNLINKED",
        "BANK_ACCOUNT_INACTIVATED"
      ]
    };
  },

  mounted() {
    this.fetchWebhookConfig();
  },

  methods: {
    fetchWebhookConfig() {
      this.isInitialLoading = true;
      MerchantConfigService.getWebhook()
        .then((response) => {
          if (response.data && response.data.data) {
            const webhookData = response.data.data;
            if (webhookData.active !== undefined) {
              webhookData.active = parseInt(webhookData.active, 10);
            }
            this.webhook = webhookData;
          } else {
            this.webhook = null;
          }
          this.isInitialLoading = false;
        })
        .catch((error) => {
          this.isInitialLoading = false;
          console.error("Lỗi khi lấy cấu hình webhook:", error);
          this.$message.error("Không thể lấy cấu hình webhook. Vui lòng thử lại.");
        });
    },

    showEditDialog() {
      if (this.webhook) {
        const activeValue = parseInt(this.webhook.active, 10);
        this.formWebhook = {
          webhook_url: this.webhook.webhook_url || "",
          auth_type: this.webhook.auth_type || "SECRET_KEY",
          secret_key: this.webhook.secret_key || "",
          active: isNaN(activeValue) ? 1 : activeValue,
          allow_events: this.webhook.allow_events ? [...this.webhook.allow_events] : []
        };
        this.selectAllEvents = this.webhook.allow_events && this.webhook.allow_events.includes("*");
      } else {
        this.resetWebhookForm();
      }
      this.dialogEditWebhook = true;
    },

    saveWebhook() {
      if (!this.formWebhook.webhook_url) {
        this.$message.warning("Vui lòng nhập Webhook URL");
        return;
      }

      try {
        new URL(this.formWebhook.webhook_url);
      } catch (error) {
        this.$message.warning("Webhook URL không hợp lệ");
        return;
      }

      if (!this.formWebhook.secret_key) {
        this.$message.warning("Vui lòng nhập Secret Key");
        return;
      }

      if (this.formWebhook.secret_key.length < 8) {
        this.$message.warning("Secret Key phải tối thiểu 8 ký tự");
        return;
      }

      const payload = {
        webhook_url: this.formWebhook.webhook_url,
        auth_type: this.formWebhook.auth_type,
        secret_key: this.formWebhook.secret_key,
        active: parseInt(this.formWebhook.active, 10),
        allow_events: this.selectAllEvents ? ["*"] : this.formWebhook.allow_events
      };

      this.isSavingWebhook = true;

      MerchantConfigService.updateWebhook(payload)
        .then(() => {
          this.$message.success("Webhook được lưu thành công");
          this.dialogEditWebhook = false;
          this.fetchWebhookConfig();
          this.isSavingWebhook = false;
        })
        .catch((error) => {
          this.isSavingWebhook = false;
          console.error("Lỗi khi lưu webhook:", error);

          if (error.response && error.response.data && error.response.data.message) {
            this.$message.error(error.response.data.message);
          } else {
            this.$message.error("Lưu webhook thất bại. Vui lòng kiểm tra lại URL hoặc phân quyền sự kiện.");
          }
        });
    },

    resetWebhookForm() {
      this.formWebhook = {
        webhook_url: "",
        auth_type: "SECRET_KEY",
        secret_key: "",
        active: 1,
        allow_events: []
      };
      this.selectAllEvents = false;
    },

    toggleAllEvents(value) {
      if (value) {
        this.formWebhook.allow_events = [];
      }
    },

    quickUpdateAll() {
      if (!this.webhook) {
        this.$message.warning("Vui lòng tạo webhook trước");
        return;
      }

      this.$confirm("Đăng ký nhận tất cả sự kiện qua Webhook?", "Xác nhận đối soát", {
        confirmButtonText: "Đăng ký",
        cancelButtonText: "Hủy bỏ",
        type: "info"
      })
        .then(() => {
          this.isSavingWebhook = true;

          const payload = {
            webhook_url: this.webhook.webhook_url,
            auth_type: this.webhook.auth_type,
            active: parseInt(this.webhook.active, 10),
            allow_events: ["*"]
          };

          MerchantConfigService.updateWebhook(payload)
            .then(() => {
              this.$message.success("Đã đăng ký tất cả sự kiện");
              this.fetchWebhookConfig();
              this.isSavingWebhook = false;
            })
            .catch((error) => {
              this.isSavingWebhook = false;
              console.error(error);
              this.$message.error("Cập nhật đăng ký thất bại.");
            });
        })
        .catch(() => {});
    },

    quickUpdateNone() {
      if (!this.webhook) {
        this.$message.warning("Vui lòng tạo webhook trước");
        return;
      }

      this.$confirm("Hủy đăng ký tất cả các sự kiện hiện tại?", "Xác nhận hủy bỏ", {
        confirmButtonText: "Hủy bỏ",
        cancelButtonText: "Quay lại",
        type: "warning"
      })
        .then(() => {
          this.isSavingWebhook = true;

          const payload = {
            webhook_url: this.webhook.webhook_url,
            auth_type: this.webhook.auth_type,
            active: parseInt(this.webhook.active, 10),
            allow_events: []
          };

          MerchantConfigService.updateWebhook(payload)
            .then(() => {
              this.$message.success("Đã hủy đăng ký tất cả sự kiện");
              this.fetchWebhookConfig();
              this.isSavingWebhook = false;
            })
            .catch((error) => {
              this.isSavingWebhook = false;
              console.error(error);
              this.$message.error("Hủy đăng ký thất bại.");
            });
        })
        .catch(() => {});
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-check-container {
  padding: 16px;
  background-color: #f4f6fa;
  min-height: calc(100vh - 120px);
}

/* Header Banner */
.ai-header-banner {
  position: relative;
  border-radius: 12px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 24px 32px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(30, 60, 114, 0.15);

  .banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  }

  .banner-content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
    width: 100%;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .ai-icon-pulse {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
    animation: iconPulse 3s infinite ease-in-out;

    i {
      font-size: 32px;
      color: #fff;
    }
  }

  .banner-title {
    color: #fff;
    font-size: 22px;
    margin: 0 0 6px 0;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .banner-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    margin: 0;
  }
}

@keyframes iconPulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 15px rgba(255, 255, 255, 0.2); }
  50% { transform: scale(1.08); box-shadow: 0 0 25px rgba(255, 255, 255, 0.4); }
}

.loading-container {
  min-height: 300px;
}

/* Panel and Glass Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.panel-container {
  background: #fff;
}

/* Tabs customization */
.modern-tabs {
  /deep/ .el-tabs__header {
    border-bottom: 2px solid #e2e8f0;
    margin-bottom: 20px;
  }
  
  /deep/ .el-tabs__nav {
    border: none !important;
    background: transparent !important;
  }

  /deep/ .el-tabs__item {
    border: none !important;
    font-weight: 600;
    color: #64748b !important;
    border-radius: 6px 6px 0 0;
    transition: all 0.3s;
    
    &.is-active {
      color: #3b82f6 !important;
      background: #eff6ff !important;
      border-bottom: 2px solid #3b82f6 !important;
    }
  }
}

.tab-inner-content {
  padding: 8px 0;
}

/* Hints */
.hint-panel {
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;

  .hint-title {
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hint-list {
    margin: 0;
    padding-left: 20px;
    color: #475569;
  }

  &.blue-hint {
    background-color: #f0f9ff;
    border-left: 4px solid #3b82f6;
    .hint-title { color: #1d4ed8; }
  }

  &.orange-hint {
    background-color: #fffbeb;
    border-left: 4px solid #f59e0b;
    .hint-title { color: #b45309; }
  }
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
  letter-spacing: 0.3px;
}

/* Webhook status cards */
.webhook-status-card {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  
  .status-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #cbd5e1;
    padding-bottom: 12px;
  }

  .status-tag {
    font-weight: 600;
    font-size: 12px;
    border-radius: 6px;
  }

  .status-grid-info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .info-item {
    background: #fff;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .info-label {
    font-size: 12px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
  }

  .info-val {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    word-break: break-all;

    &.code-font {
      font-family: Consolas, Monaco, monospace;
      color: #2563eb;
    }
    &.text-password {
      color: #94a3b8;
      letter-spacing: 2px;
    }
  }
}

.registered-events-section {
  .events-tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    background: #fff;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .event-tag-chip {
    font-weight: 600;
    font-size: 12px;
    font-family: Consolas, Monaco, monospace;
  }
}

/* Empty state card */
.empty-state-card {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  background-color: #f8fafc;
  
  i {
    font-size: 36px;
    color: #94a3b8;
    margin-bottom: 10px;
  }

  .empty-text {
    margin: 0;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
}

/* Event categories reference */
.event-category-block {
  .category-title {
    font-size: 14px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 12px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .events-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }

  .event-card-item {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    transition: all 0.3s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.02);

    &:hover {
      transform: translateY(-2px);
      border-color: #3b82f6;
      box-shadow: 0 4px 10px rgba(59, 130, 246, 0.08);
    }

    .ev-code {
      display: block;
      font-family: Consolas, Monaco, monospace;
      font-size: 12px;
      font-weight: 700;
      color: #3b82f6;
      background: #eff6ff;
      padding: 4px 8px;
      border-radius: 4px;
      border: 1px solid #dbeafe;
      margin-bottom: 8px;
      word-break: break-all;
    }

    .ev-desc {
      margin: 0;
      font-size: 12px;
      color: #64748b;
      line-height: 1.5;
    }
  }
}

/* Quick action bar */
.quick-actions-bar {
  border-top: 1px solid #e2e8f0;
}

.gradient-btn-small {
  border: none;
  font-weight: 600;
  padding: 9px 18px;
  border-radius: 6px;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-1px);
  }

  &.blue {
    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
    box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
  }

  &.green {
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
    box-shadow: 0 4px 10px rgba(16, 185, 129, 0.3);
  }
}

.cancel-upload-btn {
  border-radius: 6px;
  padding: 9px 18px;
  margin-left: 8px;
}

/* Switch status wrapper */
.switch-status-wrapper {
  display: flex;
  align-items: center;
  height: 40px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0 12px;
}

/* Checkbox Grid for Dialog */
.events-picker-box {
  background-color: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 16px;

  .checkbox-grid-events {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
    transition: opacity 0.3s;
  }

  .checkbox-item {
    margin-right: 0 !important;
    /deep/ .el-checkbox__label {
      font-family: Consolas, Monaco, monospace;
      font-size: 11px;
      font-weight: 600;
    }
  }
}

.opacity-50 {
  opacity: 0.5;
  pointer-events: none;
}

.mb-0 { margin-bottom: 0; }
.mb-2 { margin-bottom: 8px; }
.mb-3 { margin-bottom: 12px; }
.mb-4 { margin-bottom: 16px; }
.mt-3 { margin-top: 12px; }
.mt-4 { margin-top: 16px; }
.pt-2 { padding-top: 8px; }
.pt-3 { padding-top: 12px; }
.block { display: block; }
.ml-2 { margin-left: 8px; }
.font-semibold { font-weight: 600; }
.font-bold { font-weight: 700; }
.flex { display: flex; }
.flex-column { flex-direction: column; }
.justify-center { justify-content: center; }
.align-center { align-items: center; }
</style>
